import React, { useState, useEffect, useRef } from "react";
import "./newPricing.css";
import NavigationBar from "../NewLandingPage/NavigationBar";

function NewPricing() {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const canvasRef = useRef(null);

  const pricingPlans = [
    {
      name: "Basic",
      monthlyPrice: 99,
      annualPrice: Math.round((99 * 12 * 0.75) / 12),
      badge: null,
    },
    {
      name: "Pro",
      monthlyPrice: 199,
      annualPrice: Math.round((199 * 12 * 0.75) / 12),
      badge: "Most Popular",
    },
    {
      name: "Enterprise",
      monthlyPrice: 399,
      annualPrice: Math.round((399 * 12 * 0.75) / 12),
      badge: null,
    },
  ];

  const workflowFeatures = [
    { feature: "R&O Identification", basic: true, pro: true, enterprise: true },
    {
      feature: "Generated Action Plans",
      basic: true,
      pro: true,
      enterprise: true,
    },
    {
      feature: "Integration into Workflow",
      basic: true,
      pro: true,
      enterprise: true,
    },
    {
      feature: "Generated Resources",
      basic: false,
      pro: true,
      enterprise: true,
    },
    {
      feature: "Financial Analysis",
      basic: false,
      pro: true,
      enterprise: true,
    },
    {
      feature: "Execution Automation",
      basic: false,
      pro: true,
      enterprise: true,
    },
    { feature: "AI Optimization", basic: false, pro: false, enterprise: true },
    {
      feature: "Full-Scale Integrations",
      basic: false,
      pro: false,
      enterprise: true,
    },
  ];

  const strategicFeatures = [
    { feature: "Priority Mapping", basic: true, pro: true, enterprise: true },
    {
      feature: "Customizable Dashboards",
      basic: true,
      pro: true,
      enterprise: true,
    },
    {
      feature: "Budget Tracking & Alerts",
      basic: true,
      pro: true,
      enterprise: true,
    },
    {
      feature: "Forecasting Success Rates",
      basic: true,
      pro: true,
      enterprise: true,
    },
    {
      feature: "Objective-Based Guidance",
      basic: false,
      pro: true,
      enterprise: true,
    },
    {
      feature: "Decision Comparisons",
      basic: false,
      pro: true,
      enterprise: true,
    },
    {
      feature: "Competitor Strategy Simulator",
      basic: false,
      pro: true,
      enterprise: true,
    },
    {
      feature: "Advanced Reporting Tools",
      basic: false,
      pro: false,
      enterprise: true,
    },
    {
      feature: "Consultations / Support",
      basic: false,
      pro: false,
      enterprise: true,
    },
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationFrameId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    let waveOffset = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        for (let x = 0; x < canvas.width; x += 5) {
          const y =
            canvas.height / 2 + Math.sin(x * 0.01 + waveOffset + i) * 50;
          ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(192, 192, 192, ${0.12 - i * 0.03})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      waveOffset += 0.01;
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <>
    <NavigationBar />
      <div className="pricing-page">
        <canvas ref={canvasRef} className="background-canvas" />
        <div className="pricing-header">
          <h1>Join Today</h1>
          <button className="cta-button">
            <span>Start Your 14-Day Free Trial</span>
          </button>
          <p className="cta-subtext">
            Free trial gives access to the Pro Plan.
          </p>
        </div>

        <div className="pricing-cards">
          <div className="billing-toggle">
            <span className={billingCycle === "monthly" ? "active" : ""}>
              Monthly
            </span>
            <label className="switch">
              <input
                type="checkbox"
                checked={billingCycle === "annual"}
                onChange={() =>
                  setBillingCycle(
                    billingCycle === "monthly" ? "annual" : "monthly"
                  )
                }
              />
              <span className="slider"></span>
            </label>
            <span className={billingCycle === "annual" ? "active" : ""}>
              Annual (25% Off)
            </span>
          </div>

          <div className="cards-grid">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`pricing-card ${plan.name.toLowerCase()} ${
                  plan.badge ? "recommended" : ""
                }`}
              >
                {plan.badge && <span className="badge">{plan.badge}</span>}
                <h3>{plan.name}</h3>
                <p className="price">
                  $
                  {billingCycle === "monthly"
                    ? plan.monthlyPrice
                    : plan.annualPrice}
                  /mo
                </p>
                <p className="annual-text">
                  {billingCycle === "monthly"
                    ? `$${plan.annualPrice}/month with annual billing (25% off)`
                    : `Billed annually at $${plan.annualPrice * 12}`}
                </p>
                <button className="select-plan">
                  <span>Select Plan</span>
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="feature-tables">
          <h2>Workflow Features</h2>
          <table className="feature-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Basic</th>
                <th>Pro</th>
                <th>Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {workflowFeatures.map((feature, index) => (
                <tr key={index}>
                  <td>{feature.feature}</td>
                  <td className="basic">{feature.basic ? "✔️" : "—"}</td>
                  <td className="pro">{feature.pro ? "✔️" : "—"}</td>
                  <td className="enterprise">
                    {feature.enterprise ? "✔️" : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Strategic & Analytical Features</h2>
          <table className="feature-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Basic</th>
                <th>Pro</th>
                <th>Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {strategicFeatures.map((feature, index) => (
                <tr key={index}>
                  <td>{feature.feature}</td>
                  <td className="basic">{feature.basic ? "✔️" : "—"}</td>
                  <td className="pro">{feature.pro ? "✔️" : "—"}</td>
                  <td className="enterprise">
                    {feature.enterprise ? "✔️" : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default NewPricing;
